import React from "react";
import {
  Group,
  Stack,
  Text,
  Paper,
  ActionIcon,
  Menu,
  TextInput,
} from "@mantine/core";
import {
  IconDotsVertical,
  IconPencil,
  IconStar,
  IconTrash,
  IconArrowRight,
} from "@tabler/icons-react";
import { ClientPaymentMethod, isCardExpired } from "../../types/cardOnFile";
import styles from "./PaymentMethodCard.module.css";

function brandChipClass(brand: string): string {
  const b = brand.toLowerCase();
  if (b.includes("visa")) return styles.chipVisa;
  if (b.includes("master")) return styles.chipMastercard;
  if (b.includes("amex") || b.includes("american")) return styles.chipAmex;
  if (b.includes("discover")) return styles.chipDiscover;
  return styles.chipGeneric;
}

function brandChipLabel(brand: string): string {
  const b = brand.toLowerCase();
  if (b.includes("visa")) return "VISA";
  if (b.includes("master")) return "MAST";
  if (b.includes("amex") || b.includes("american")) return "AMEX";
  if (b.includes("discover")) return "DISC";
  return brand.slice(0, 4).toUpperCase();
}

export interface PaymentMethodCardProps {
  card: ClientPaymentMethod;
  clientDisplayName: string;
  readOnly: boolean;
  editingNicknamePmId: number | null;
  nicknameDraft: string;
  nicknameError: string | null;
  isSavingNickname: boolean;
  isDeleting: boolean;
  isSettingDefault: boolean;
  onStartNicknameEdit: (card: ClientPaymentMethod) => void;
  onSaveNickname: (pmId: number) => void;
  onCancelNicknameEdit: () => void;
  onNicknameDraftChange: (value: string) => void;
  onSetDefault: (card: ClientPaymentMethod) => void;
  onDelete: (card: ClientPaymentMethod) => void;
}

export default function PaymentMethodCard({
  card,
  clientDisplayName,
  readOnly,
  editingNicknamePmId,
  nicknameDraft,
  nicknameError,
  isSavingNickname,
  isDeleting,
  isSettingDefault,
  onStartNicknameEdit,
  onSaveNickname,
  onCancelNicknameEdit,
  onNicknameDraftChange,
  onSetDefault,
  onDelete,
}: PaymentMethodCardProps) {
  const expired = isCardExpired(card);
  const masked = `•••• •••• •••• ${card.cardLast4}`;

  return (
    <Paper
      radius="md"
      withBorder
      p="lg"
      className={`${styles.root} ${card.isDefault ? styles.rootDefault : styles.rootSecondary} ${expired ? styles.rootExpired : ""}`}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Stack gap="sm" className={styles.body}>
          {editingNicknamePmId === card.id ? (
            <TextInput
              value={nicknameDraft}
              onChange={(e) => onNicknameDraftChange(e.currentTarget.value)}
              placeholder="Enter nickname"
              size="sm"
              error={
                nicknameError ||
                (nicknameDraft.length > 64 ? "Max 64 characters" : null)
              }
              rightSection={
                <ActionIcon
                  variant="subtle"
                  color="teal"
                  onClick={() => onSaveNickname(card.id)}
                  loading={isSavingNickname}
                  aria-label="Save nickname"
                >
                  <IconArrowRight size={16} />
                </ActionIcon>
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onSaveNickname(card.id);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  onCancelNicknameEdit();
                }
              }}
            />
          ) : (
            <>
              <Group gap="sm" wrap="wrap" align="center">
                <Text fw={700} size="md" className={styles.brandName}>
                  {card.nickname?.trim()
                    ? card.nickname.trim()
                    : card.cardBrand}
                </Text>
                {card.isDefault && (
                  <span className={styles.defaultBadge}>Default</span>
                )}
                {expired && (
                  <span className={styles.expiredBadge}>Expired</span>
                )}
              </Group>
              <Group
                align="center"
                wrap="nowrap"
                gap="sm"
                className={styles.numberRow}
              >
                <div
                  className={`${styles.brandChip} ${brandChipClass(card.cardBrand)}`}
                  aria-hidden
                >
                  {brandChipLabel(card.cardBrand)}
                </div>
                <Text className={styles.maskedNumber} component="p">
                  {masked}
                </Text>
              </Group>
              <Group gap="md" wrap="wrap" className={styles.metaRow}>
                <Text size="sm" c="dimmed">
                  Expires {card.cardExpMonth.toString().padStart(2, "0")}/
                  {card.cardExpYear.toString().slice(-2)}
                </Text>
                <Text size="sm" c="dimmed">
                  {clientDisplayName}
                </Text>
              </Group>
            </>
          )}
        </Stack>

        {!readOnly && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Card options"
              >
                <IconDotsVertical size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconPencil size={14} />}
                onClick={() => onStartNicknameEdit(card)}
                disabled={
                  isDeleting ||
                  isSettingDefault ||
                  isSavingNickname ||
                  editingNicknamePmId === card.id
                }
              >
                {card.nickname ? "Edit nickname" : "Add nickname"}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconStar size={14} />}
                onClick={() => onSetDefault(card)}
                disabled={
                  card.isDefault || expired || isDeleting || isSettingDefault
                }
              >
                Set as Default
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={() => onDelete(card)}
                disabled={isDeleting || isSettingDefault}
              >
                Remove Card
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
    </Paper>
  );
}
